/**
 * Login Form Component
 *
 * Email/password login form with validation and error handling
 */

import { zodResolver } from '@hookform/resolvers/zod'
// Import schemas from @monorepo/shared
import { type Login, LoginSchema } from '@monorepo/shared/schemas/auth'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { supabase } from '@/shared/lib/supabase/client'
import { toast } from '@/shared/lib/toast'
import { cn } from '@/shared/lib/utils'
import { useAuthStore } from '../stores/authStore'

interface LoginFormProps {
  className?: string
  onSuccess?: () => void
}

export function LoginForm({ className, onSuccess }: LoginFormProps) {
  const [showPassword, setShowPassword] = useState(false)
  const [showForgotPassword, setShowForgotPassword] = useState(false)
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('')
  const [resetEmailSent, setResetEmailSent] = useState(false)

  const { login, isLoading, error, clearError } = useAuthStore()

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<Login>({
    resolver: zodResolver(LoginSchema),
    mode: 'onChange',
  })

  const onSubmit = async (data: Login) => {
    try {
      clearError()
      await login(data.email, data.password)
      onSuccess?.()
    } catch (error) {
      // Error is already handled by the store
      toast.error(extractErrorMessage(error))
    }
  }

  const handleGoogleLogin = async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
  }

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) return

    try {
      // TODO: Implement forgot password API call
      // await authApi.resetPassword(forgotPasswordEmail)
      setResetEmailSent(true)
      setTimeout(() => {
        setResetEmailSent(false)
        setShowForgotPassword(false)
        setForgotPasswordEmail('')
      }, 3000)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  if (showForgotPassword) {
    return (
      <div className={cn('flex flex-col gap-6', className)}>
        <div className='flex flex-col items-center gap-2 text-center'>
          <h1 className='text-2xl font-bold'>Reset your password</h1>
          <p className='text-muted-foreground text-sm text-balance'>
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {resetEmailSent ? (
          <div className='text-center'>
            <div className='p-4 bg-green-50 dark:bg-green-950 rounded-md border border-green-200 dark:border-green-800'>
              <p className='text-green-700 dark:text-green-300 text-sm'>
                Password reset email sent! Check your inbox.
              </p>
            </div>
          </div>
        ) : (
          <div className='grid gap-6'>
            <div className='grid gap-3'>
              <Label htmlFor='reset-email'>Email</Label>
              <Input
                id='reset-email'
                type='email'
                placeholder='Enter your email address'
                value={forgotPasswordEmail}
                onChange={e => setForgotPasswordEmail(e.target.value)}
                required
              />
            </div>
            <Button
              onClick={handleForgotPassword}
              disabled={!forgotPasswordEmail}
              className='w-full'
            >
              Send Reset Link
            </Button>
          </div>
        )}

        <div className='text-center'>
          <button
            type='button'
            onClick={() => setShowForgotPassword(false)}
            className='text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline'
          >
            Back to login
          </button>
        </div>
      </div>
    )
  }

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={cn('flex flex-col gap-6', className)}
    >
      <div className='flex flex-col items-center gap-2 text-center'>
        <h1 className='text-2xl font-bold'>Login to your account</h1>
        <p className='text-muted-foreground text-sm text-balance'>
          Login to start monetizing your AI agents
        </p>
      </div>

      {error && (
        <div className='p-4 bg-red-50 dark:bg-red-950 rounded-md border border-red-200 dark:border-red-800'>
          <p className='text-red-700 dark:text-red-300 text-sm'>{error}</p>
        </div>
      )}

      <div className='grid gap-6'>
        <div className='grid gap-3'>
          <Label htmlFor='email'>Email</Label>
          <Input
            id='email'
            type='email'
            placeholder='Enter your email'
            {...register('email')}
            className={errors.email ? 'border-red-500' : ''}
          />
          {errors.email && (
            <p className='text-sm text-red-500'>{errors.email.message}</p>
          )}
        </div>

        <div className='grid gap-3'>
          <div className='flex items-center justify-between'>
            <Label htmlFor='password'>Password</Label>
            <button
              type='button'
              onClick={() => setShowForgotPassword(true)}
              className='text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline'
            >
              Forgot password?
            </button>
          </div>
          <div className='relative'>
            <Input
              id='password'
              type={showPassword ? 'text' : 'password'}
              placeholder='Enter your password'
              {...register('password')}
              className={errors.password ? 'border-red-500 pr-10' : 'pr-10'}
            />
            <button
              type='button'
              onClick={() => setShowPassword(!showPassword)}
              className='absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground'
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className='text-sm text-red-500'>{errors.password.message}</p>
          )}
        </div>

        <Button
          type='submit'
          className='w-full'
          disabled={!isValid || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              Signing in...
            </>
          ) : (
            'Sign in'
          )}
        </Button>

        <div className='relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t after:border-border'>
          <span className='bg-background text-muted-foreground relative z-10 px-2'>
            Or continue with
          </span>
        </div>

        <Button
          type='button'
          variant='outline'
          className='w-full'
          disabled={isLoading}
          onClick={handleGoogleLogin}
        >
          <svg className='mr-2 h-4 w-4' viewBox='0 0 24 24'>
            <title>Google</title>
            <path
              d='M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z'
              fill='#4285F4'
            />
            <path
              d='M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z'
              fill='#34A853'
            />
            <path
              d='M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z'
              fill='#FBBC05'
            />
            <path
              d='M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z'
              fill='#EA4335'
            />
          </svg>
          Continue with Google
        </Button>
      </div>

      <div className='text-center text-sm'>
        Don't have an account?{' '}
        <a
          href='/signup'
          className='underline underline-offset-4 hover:text-foreground'
        >
          Sign up
        </a>
      </div>
    </form>
  )
}
